// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

// --- HS Code
import {
  DemographicValueModel,
  UserDemographicSelectList,
  SelectListItem,
  DemographicModel,
} from '../../../models';
import { AppService } from '../app.service';
import { DemographicsBaseService } from '../system/demographics-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class UserDemographicsBaseService implements OnDestroy {
  protected _demographicsSubscription: Subscription;
  private _demographics: DemographicModel[] | undefined;

  protected _userDemographicValues$: BehaviorSubject<DemographicValueModel[]>;
  get userDemographicValues$(): Observable<DemographicValueModel[]> {
    return this._userDemographicValues$;
  }

  protected _userDemographicsList$: BehaviorSubject<
    UserDemographicSelectList[]
  >;
  get userDemographicsList$(): Observable<UserDemographicSelectList[]> {
    return this._userDemographicsList$;
  }

  protected _updateUserDemographicsResult$: Subject<boolean>;
  get updateUserDemographicsResult$(): Observable<boolean> {
    return this._updateUserDemographicsResult$;
  }

  constructor(
    protected appService: AppService,
    protected demographicService: DemographicsBaseService
  ) {
    this._userDemographicValues$ = new BehaviorSubject<DemographicValueModel[]>(
      []
    );
    this._userDemographicsList$ = new BehaviorSubject<
      UserDemographicSelectList[]
    >([]);
    this._updateUserDemographicsResult$ = new Subject<boolean>();

    this._demographicsSubscription =
      this.demographicService.demographics$.subscribe((demographics) => {
        this._demographics = demographics;
        const userDemographicValues = this._userDemographicValues$.value;
        if (userDemographicValues) {
          this._processUserDemographics(userDemographicValues);
        }
      });
  }

  ngOnDestroy(): void {
    if (this._demographicsSubscription) {
      this._demographicsSubscription.unsubscribe();
      this._demographicsSubscription = Subscription.EMPTY;
    }
  }

  abstract updateUserDemographicsFromProfile(
    userDemographicValues: DemographicValueModel[]
  ): void;
  abstract loadFromUserDetails(userDemographicValues: DemographicValueModel[]): void;

  protected _processUserDemographics(
    userDemographicValues: DemographicValueModel[]
  ) {
    if (!this._demographics) {
      return;
    }

    const list = new Array<UserDemographicSelectList>();

    for (const demographic of this._demographics) {
      const userDemographicValue = userDemographicValues.find(
        (udv) => udv.demographicId === demographic.id
      );
      const itemsList = new Array<SelectListItem>();

      for (const value of demographic.values) {
        itemsList.push({
          isDisabled: false,
          isSelected:
            userDemographicValue && userDemographicValue.id === value.id,
          text: value.name,
          value: value.id,
        } as SelectListItem);
      }

      const item = {
        demographics: itemsList,
        id: demographic.id,
        identifier: demographic.moniker,
        name: demographic.name,
        validationMessage: '',
        selectedItem: userDemographicValue && userDemographicValue.id,
      } as UserDemographicSelectList;
      list.push(item);
    }

    this._userDemographicsList$.next(list);
  }
}
